const MAX_HISTORY = 100;
function cloneDrawings(drawings) {
    return drawings.map((drawing) => ({
        ...drawing,
        points: drawing.points.map((point) => ({ ...point })),
        style: { ...drawing.style },
        // P1-11: legacy `fibLevels` was shared by reference across undo nodes —
        // editing fib levels on one node leaked into the others.
        fibLevels: drawing.fibLevels ? [...drawing.fibLevels] : undefined,
        fibLevelConfigs: drawing.fibLevelConfigs?.map((c) => ({ ...c })),
        segmentStyles: drawing.segmentStyles
            ? Object.fromEntries(Object.entries(drawing.segmentStyles).map(([key, value]) => [key, { ...value }]))
            : undefined,
        segmentVisible: drawing.segmentVisible ? { ...drawing.segmentVisible } : undefined,
        riskReward: drawing.riskReward ? { ...drawing.riskReward } : undefined,
        visibility: drawing.visibility
            ? {
                ...drawing.visibility,
                timeframes: drawing.visibility.timeframes ? [...drawing.visibility.timeframes] : undefined,
                symbols: drawing.visibility.symbols ? [...drawing.visibility.symbols] : undefined,
            }
            : undefined,
        alertCondition: drawing.alertCondition ? { ...drawing.alertCondition } : undefined,
        alertConditions: drawing.alertConditions?.map((c) => ({ ...c })),
        waveLabels: drawing.waveLabels ? [...drawing.waveLabels] : undefined,
        harmonicRatios: drawing.harmonicRatios
            ? {
                ...drawing.harmonicRatios,
                targetRatios: drawing.harmonicRatios.targetRatios?.map((band) => [...band]),
            }
            : undefined,
        cycleParams: drawing.cycleParams ? { ...drawing.cycleParams } : undefined,
    }));
}
export function createDrawingHistory(drawings = []) {
    return {
        past: [],
        present: cloneDrawings(drawings),
        future: [],
    };
}
export function historyReducer(state, action) {
    switch (action.type) {
        case "PUSH":
            return {
                past: [...state.past, cloneDrawings(state.present)].slice(-MAX_HISTORY),
                present: [...state.present, action.drawing],
                future: [],
            };
        case "REPLACE":
            // P1-11/D-03: every new intent clears the redo stack. Keeping `future`
            // across edits resurrected stale pre-edit states on redo.
            return {
                past: [...state.past, cloneDrawings(state.present)].slice(-MAX_HISTORY),
                present: cloneDrawings(action.drawings),
                future: [],
            };
        case "RECONCILE":
            // P3-07: present is re-derived, the timeline is untouched. `past` and
            // `future` keep their identity so React can skip re-rendering the
            // history consumers when only the anchors moved.
            return {
                past: state.past,
                present: cloneDrawings(action.drawings),
                future: state.future,
            };
        case "RESET_SCOPE":
            // M2: a scope change invalidates every recorded step, so the timeline
            // is dropped rather than carried. `present` is still seeded from the
            // new scope — an empty past/future is what makes undo a no-op here
            // rather than a leak.
            return {
                past: [],
                present: cloneDrawings(action.drawings),
                future: [],
            };
        case "REORDER": {
            // Reorder present drawings to match the provided id sequence (z-order / bring-to-front etc.)
            const byId = new Map(state.present.map((d) => [d.id, d]));
            const reordered = [];
            for (const id of action.ids) {
                const found = byId.get(id);
                if (found) {
                    reordered.push(found);
                    byId.delete(id);
                }
            }
            // Append any drawings not present in the id list (safety — preserves all)
            for (const drawing of state.present) {
                if (byId.has(drawing.id)) {
                    reordered.push(drawing);
                }
            }
            return {
                past: [...state.past, cloneDrawings(state.present)].slice(-MAX_HISTORY),
                present: cloneDrawings(reordered),
                future: [],
            };
        }
        case "UNDO": {
            if (state.past.length === 0) {
                return state;
            }
            const nextPast = state.past.slice(0, -1);
            const previousPresent = state.past[state.past.length - 1];
            return {
                past: nextPast,
                present: cloneDrawings(previousPresent),
                future: [cloneDrawings(state.present), ...state.future],
            };
        }
        case "REDO": {
            if (state.future.length === 0) {
                return state;
            }
            const [nextPresent, ...remainingFuture] = state.future;
            return {
                // P1-11: cap the past push like every other intent (was unbounded).
                past: [...state.past, cloneDrawings(state.present)].slice(-MAX_HISTORY),
                present: cloneDrawings(nextPresent),
                future: remainingFuture,
            };
        }
        case "CLEAR":
            return {
                past: [...state.past, cloneDrawings(state.present)].slice(-MAX_HISTORY),
                present: [],
                future: [],
            };
        default:
            return state;
    }
}
